import React, { useEffect, useRef } from 'react';
import AutomatedTask from '../assets/AutomatedTask.webp';
import AdvancedConfiguration from '../assets/AdvancedConfiguration.webp';
import providerReputation from '../assets/providerReputation.webp';
import revenueOptimization from '../assets/revenueOptimization.webp';
import distributedAIComputing from '../assets/distributedAIComputing.webp';
import flexibleGPUutilization from '../assets/flexibleGPUutilization.webp';
import CurvedLinesCanvas from './CurvedLinesCanvas.js';
import CurvedLinesCanvas2 from './CurvedLinesCanvas2.js';
import CurvedLinesCanvas3 from './CurvedLinesCanvas3.js';
import CurvedLinesCanvas4 from './CurvedLinesCanvas4.js';
import '../css/Provider.css';

const AIDePINProvider = () => {
  const featureProviderRefs = useRef([]);

  useEffect(() => {
    const setEqualHeights = () => {
      const heights = featureProviderRefs.current.map(ref => ref?.offsetHeight || 0);
      const maxHeight = Math.max(...heights);
      featureProviderRefs.current.forEach(ref => {
        if (ref) {
          ref.style.height = `${maxHeight}px`;
        }
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    featureProviderRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    // Utilisation de setTimeout pour s'assurer que le calcul se fait après le rendu complet
    setTimeout(() => {
      setEqualHeights();
    }, 300); // Petit délai pour forcer le recalcul des hauteurs après le rendu

    window.addEventListener('resize', setEqualHeights);

    return () => {
      featureProviderRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
      window.removeEventListener('resize', setEqualHeights);
    };
  }, []);

  return (
    <section id="ai-depin-provider" className="provider-section">
      <h2>AI-DePIN Provider: Main Features</h2>

      <div className="provider-features">
        {/* Ligne 1 - Feature 1, 2, et 3 */}
        <div className="feature-provider-row">
          <div className="feature-provider-item slide-left" ref={(el) => (featureProviderRefs.current[0] = el)}>
            <CurvedLinesCanvas4 />
            <div className="feature-provider-content">
              <h3>Distributed AI Computing</h3>
              <div className="feature-provider-image-wrapper">
                <img src={distributedAIComputing} alt="distributedAIComputing" className="feature-provider-image" />
              </div>
                <p>As a provider, you can contribute your GPUs to AI-DePIN's distributed network for large-scale AI computation projects. The platform connects your GPUs with businesses and startups needing computing power, earning you revenue for each task.</p>
                <p>This allows you to transform idle hardware into a source of income while supporting advanced AI tasks through decentralized computing.</p>
            </div>
          </div>

          <div className="feature-provider-item slide-top" ref={(el) => (featureProviderRefs.current[1] = el)}>
            <div className="feature-provider-content">
              <h3>Flexible GPU Utilization</h3>
              <div className="feature-provider-image-wrapper">
                <img src={flexibleGPUutilization} alt="flexibleGPUutilization" className="feature-provider-image" />
              </div>
              <p>AI-DePIN Providers can allocate their GPUs either for cryptocurrency mining or for AI computing tasks, offering flexibility to choose the most profitable option based on their preferences or market conditions.</p>
              <p>Providers can switch between both types of workloads depending on what suits their business goals best, making AI-DePIN a versatile solution for GPU owners looking to optimize their hardware for various tasks.</p>
            </div>
          </div>

          <div className="feature-provider-item slide-right" ref={(el) => (featureProviderRefs.current[2] = el)}>
            <CurvedLinesCanvas3 />
            <div className="feature-provider-content">
              <h3>Automated Task Management</h3>
              <div className="feature-provider-image-wrapper">
                <img src={AutomatedTask} alt="AutomatedTask" className="feature-provider-image" />
              </div>
              <p>By choosing the automatic mode, AI-DePIN ensures seamless management of both mining and AI computing tasks without the need for manual intervention. When an AI task is assigned, the system automatically pauses cryptocurrency mining and resumes it once the AI task is completed.</p>
              <p>This allows providers to maximize their GPU utilization, reducing downtime and ensuring continuous workflow optimization.</p>
            </div>
          </div>
        </div>

        {/* Ligne 2 - Feature 4, 5, et 6 */}
        <div className="feature-provider-row">
          <div className="feature-provider-item slide-left" ref={(el) => (featureProviderRefs.current[3] = el)}>
            <CurvedLinesCanvas2 />
            <div className="feature-provider-content">
              <h3>Advanced Configuration Options</h3>
              <div className="feature-provider-image-wrapper">
                <img src={AdvancedConfiguration} alt="AdvancedConfiguration" className="feature-provider-image" />
              </div>
              <p>AI-DePIN offers providers comprehensive control over their GPU operations through an intuitive interface. Providers can easily configure miners, choose between solo or PPLNS mining modes, configure pool selections, and adjust task priorities.</p>
              <p>This flexibility ensures that providers maintain control over their GPUs' workloads, helping them maximize performance and revenue.</p>
            </div>
          </div>

          <div className="feature-provider-item slide-bottom" ref={(el) => (featureProviderRefs.current[4] = el)}>
            <div className="feature-provider-content">
              <h3>Provider Reputation Score</h3>
              <div className="feature-provider-image-wrapper">
                <img src={providerReputation} alt="providerReputation" className="feature-provider-image" />
              </div>
              <p>AI-DePIN allows providers to build a reputation based on the quality and reliability of the services they offer. The platform tracks performance metrics such as uptime, task completion rates, and client satisfaction, creating a reputation score that influences task assignments and potential earnings.</p>
              <p>Providers with higher scores are more likely to receive high-value AI tasks, incentivizing consistent performance and reliability.</p>
            </div>
          </div>

          <div className="feature-provider-item slide-right" ref={(el) => (featureProviderRefs.current[5] = el)}>
            <CurvedLinesCanvas />
            <div className="feature-provider-content">
              <h3>Revenue Optimization</h3>
              <div className="feature-provider-image-wrapper">
                <img src={revenueOptimization} alt="revenueOptimization" className="feature-provider-image" />
              </div>
              <p>Maximize your earnings by leveraging both cryptocurrency mining and AI computation through AI-DePIN. Providers can take advantage of AI-DePIN’s automated task-switching system, which dynamically switches between mining and AI tasks based on profitability.</p>
              <p>This ensures your GPUs are consistently engaged in the most lucrative activity, maintaining a continuous flow of income from both streams.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIDePINProvider;
